// Libraries
import { useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import { Button } from "#src/common/system/Button";
import { Input } from "#src/common/system/Input";
import { CopyToClipboardButton } from "#src/common/system/CopyToClipboardButton";
import { createToast } from "#src/common/system/toasts";
import { apiErrorHandler } from "#src/utils/apiErrorHandler";

// API-related
import assetsServices, {
  type IDomainOwnershipItem,
} from "#src/services/assets";

export const IpAddressDrawerTitle = () => {
  const { t } = useTranslation("assets", {
    keyPrefix: "ipAddress.drawer.verifyOwnership",
  })
  return (
    <h2>
      {t("title")}
    </h2>
  )
}

export const IpAddressDrawerContent = ({
  assetName,
  assetOwnership
}: {
  assetName: string | null,
  assetOwnership: IDomainOwnershipItem
}) => {
  const { t } = useTranslation("assets", {
    keyPrefix: "ipAddress.drawer.verifyOwnership",
  })
  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex flex-col gap-2">
        <h4>{t("field.domain.label")}</h4>
        <span className="text-primary font-bold-14">{assetName}</span>
      </div>
      <div className="flex flex-col gap-2">
        <h4>{t("field.host.label")}</h4>
        <div className="flex gap-1">
          <Input
            className="w-full"
            value={assetOwnership.key || ""}
            disabled
          />
          <CopyToClipboardButton value={assetOwnership.key} />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <h4>{t("field.value.label")}</h4>
        <div className="flex gap-1">
          <Input
            className="w-full"
            value={assetOwnership.value || ""}
            disabled
          />
          <CopyToClipboardButton value={assetOwnership.value} />
        </div>
      </div>
      <p className="font-regular-14 text-hard-grey whitespace-pre-line">
        {t("instruction.text", { path: `https://${assetName}/${assetOwnership.key}` })}
      </p>
      <a
        href="https://docs.cystack.net/vi/guide/websites/verify.html#xac-minh-bang-meta-tag"
        target="_blank"
        rel="noreferrer"
      >
        {t("instruction.link")}
      </a>
    </div>
  )
}

export const IpAddressDrawerFooter = ({
  workspaceId,
  assetId,
  onClose,
  onRefresh,
}: {
  workspaceId: string,
  assetId: number | null,
  onClose: () => void,
  onRefresh: () => void
}) => {
  const { t } = useTranslation("assets", {
    keyPrefix: "ipAddress.drawer.verifyOwnership",
  })

  const [pending, setPending] = useState<boolean>(false);

  const verifyOwnership = () => {
    if (assetId) {
      setPending(true);
      assetsServices
        .verify_domain(assetId, workspaceId)
        .then(() => {
          createToast({
            type: "success",
            message: t("notification.verifyAsset.success"),
          });
          onRefresh();
          onClose();
        })
        .catch((error) => {
          apiErrorHandler(error, {
            toastMessage: t("notification.verifyAsset.fail"),
          });
        })
        .finally(() => {
          setPending(false);
        });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Button
        className="w-full"
        size="large"
        pending={pending}
        onClick={() => {
          verifyOwnership();
        }}
      >
        {t("button.verifyOwnership")}
      </Button>
      <Button
        variant="secondary"
        className="w-full"
        size="large"
        onClick={onClose}
      >
        {t("button.cancel")}
      </Button>
    </div>
  )
}